// hooks/useConversations.js
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { fetchData } from "../api/client";

// helper
const getStatusLabelFromCode = (status) => {
  const statusMap = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };

  if (typeof status === "string" && isNaN(Number(status))) return status;
  const n = typeof status === "string" ? Number(status) : status;
  return statusMap[n] ?? "Desconhecido";
};

/**
 * @param {string} searchTerm - Busca textual
 * @param {string[]} filters - Filtros rápidos (Minhas Solicitações, status, etc.)
 * @param {object} [advancedParams] - tipo_scope, ids_tipos, ids_subtipo1/2/3, ids_origens, ids_usuarios, data_inicio, data_fim, date_field, default_date_window_days, include_message_text
 * @param {boolean} [requestTotal] - Se true, pede total no banco (COUNT). Evitar na 1ª carga para não travar em bases grandes.
 * @param {object} [options] - opções adicionais do react-query (ex.: enabled).
 */
export const useConversations = (
  searchTerm = "",
  filters = [],
  advancedParams = null,
  requestTotal = false,
  options = {}
) => {
  const advanced =
    advancedParams && typeof advancedParams === "object" ? advancedParams : {};

  // Debounce da busca para evitar disparar requisições a cada tecla
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm || "");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm || ""), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const effectiveSearch =
    typeof debouncedSearch === "string" && debouncedSearch.trim().length >= 2
      ? debouncedSearch.trim()
      : "";

  const normalizedFilters = useMemo(() => {
    const arr = Array.isArray(filters) ? filters : [];
    // ordena para estabilizar queryKey e reduzir cache-miss
    return [...arr].sort();
  }, [filters]);

  const normalizedAdvanced = useMemo(() => {
    const a = advanced || {};
    const normArray = (v) =>
      Array.isArray(v) ? [...v].map(String).sort() : v;
    const rawScope = String(a.tipo_scope ?? "relacionamento").trim().toLowerCase();
    const tipo_scope =
      rawScope === "comercial" || rawScope === "commercial" || rawScope === "0"
        ? "comercial"
        : rawScope === "todos" || rawScope === "ambos" || rawScope === "all"
          ? "todos"
          : "relacionamento";

    return {
      tipo_scope,
      tipo_responsavel: ["fila", "humanos", "chatbots", "agentes"].includes(
        a.tipo_responsavel
      )
        ? a.tipo_responsavel
        : "",
      ids_tipos: normArray(a.ids_tipos),
      ids_subtipo1: normArray(a.ids_subtipo1),
      ids_subtipo2: normArray(a.ids_subtipo2),
      ids_subtipo3: normArray(a.ids_subtipo3),
      ids_origens: normArray(a.ids_origens),
      ids_usuarios: normArray(a.ids_usuarios),
      data_inicio: a.data_inicio || "",
      data_fim: a.data_fim || "",
      date_field: a.date_field || "",
      default_date_window_days: a.default_date_window_days
        ? Number(a.default_date_window_days) || 0
        : 0,
      // busca em texto da mensagem: apenas quando a UI avançada ligar
      include_message_text: !!a.include_message_text,
    };
  }, [advancedParams]);

  return useInfiniteQuery(
    ["conversations", effectiveSearch, normalizedFilters, normalizedAdvanced],

    async ({ pageParam = null }) => {
      const qs = new URLSearchParams();

      if (effectiveSearch) qs.set("search", effectiveSearch);

      // API combina filtros com AND. Vários filtros em "Quem" precisam de OR no cliente.
      // Se o usuário escolheu mais de um entre Quem, não enviamos esses chips nem tipo_responsavel correspondente.
      const ownershipFilterNames = [
        "Minhas Solicitações",
        "Outros Usuários",
        "Fila",
        "ChatBots",
        "Agentes de IA",
      ];
      const ownershipSelected = (normalizedFilters || []).filter((f) =>
        ownershipFilterNames.includes(f)
      );
      const filtersForApi =
        ownershipSelected.length > 1
          ? (normalizedFilters || []).filter(
              (f) => !ownershipFilterNames.includes(f)
            )
          : normalizedFilters || [];

      if (filtersForApi?.length) qs.set("filters", filtersForApi.join(","));

      if (normalizedAdvanced.ids_tipos?.length)
        qs.set(
          "ids_tipos",
          Array.isArray(normalizedAdvanced.ids_tipos)
            ? normalizedAdvanced.ids_tipos.map(String).join(",")
            : String(normalizedAdvanced.ids_tipos)
        );
      if (normalizedAdvanced.ids_subtipo1?.length)
        qs.set(
          "ids_subtipo1",
          Array.isArray(normalizedAdvanced.ids_subtipo1)
            ? normalizedAdvanced.ids_subtipo1.map(String).join(",")
            : String(normalizedAdvanced.ids_subtipo1)
        );
      if (normalizedAdvanced.ids_subtipo2?.length)
        qs.set(
          "ids_subtipo2",
          Array.isArray(normalizedAdvanced.ids_subtipo2)
            ? normalizedAdvanced.ids_subtipo2.map(String).join(",")
            : String(normalizedAdvanced.ids_subtipo2)
        );
      if (normalizedAdvanced.ids_subtipo3?.length)
        qs.set(
          "ids_subtipo3",
          Array.isArray(normalizedAdvanced.ids_subtipo3)
            ? normalizedAdvanced.ids_subtipo3.map(String).join(",")
            : String(normalizedAdvanced.ids_subtipo3)
        );
      if (normalizedAdvanced.ids_origens?.length)
        qs.set(
          "ids_origens",
          Array.isArray(normalizedAdvanced.ids_origens)
            ? normalizedAdvanced.ids_origens.map(String).join(",")
            : String(normalizedAdvanced.ids_origens)
        );
      if (normalizedAdvanced.ids_usuarios?.length)
        qs.set(
          "ids_usuarios",
          Array.isArray(normalizedAdvanced.ids_usuarios)
            ? normalizedAdvanced.ids_usuarios.map(String).join(",")
            : String(normalizedAdvanced.ids_usuarios)
        );
      if (normalizedAdvanced.data_inicio)
        qs.set("data_inicio", normalizedAdvanced.data_inicio);
      if (normalizedAdvanced.data_fim)
        qs.set("data_fim", normalizedAdvanced.data_fim);
      if (normalizedAdvanced.date_field)
        qs.set("date_field", normalizedAdvanced.date_field);
      if (normalizedAdvanced.tipo_scope)
        qs.set("tipo_scope", normalizedAdvanced.tipo_scope);
      if (normalizedAdvanced.tipo_responsavel)
        qs.set("tipo_responsavel", normalizedAdvanced.tipo_responsavel);
      if (normalizedAdvanced.default_date_window_days)
        qs.set(
          "default_date_window_days",
          String(normalizedAdvanced.default_date_window_days)
        );
      if (normalizedAdvanced.include_message_text)
        qs.set("include_message_text", "1");

      if (pageParam?.ts && pageParam?.id) {
        qs.set("cursorTs", pageParam.ts);
        qs.set("cursorId", pageParam.id);
      }

      const resp = await fetchData(`/conversations?${qs.toString()}`, "GET");

      return {
        list: Array.isArray(resp?.data) ? resp.data : [],
        pagination: resp?.pagination || { hasMore: false, nextCursor: null },
        totalInDatabase: resp?.totalInDatabase,
      };
    },

    {
      keepPreviousData: true,
      staleTime: 10_000,
      getNextPageParam: (lastPage) => {
        return lastPage?.pagination?.hasMore
          ? lastPage.pagination.nextCursor
          : undefined;
      },
      select: (data) => {
        const pages = (data?.pages || []).map((p) => ({
          ...p,
          list: (p.list || []).map((conv) => ({
            ...conv,
            unread: Number(conv?.unread ?? 0),
            statusLabel: getStatusLabelFromCode(conv?.status),
            agentId: conv?.agentId ?? conv?.id_agente ?? null,
            agentName: conv?.agentName ?? conv?.agent_name ?? null,
            lastClientMessageAt: conv?.lastClientMessageAt ?? conv?.last_client_message_at ?? null,
            channelType: conv?.channelType ?? conv?.channel_type ?? null,
          })),
        }));

        return { ...data, pages };
      },
      ...(options || {}),
    }
  );
};
